import api from './api';

const CLOSED = new Set(['COMPLETED', 'REJECTED']);

export function toYmd(iso) {
  if (!iso) return null;
  const s = String(iso).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${day}`;
  } catch {
    return null;
  }
}

export function pickOpenJob(jobsForSs) {
  const list = Array.isArray(jobsForSs) ? jobsForSs : [];
  const open = list.filter((j) => j?.status && !CLOSED.has(j.status));
  if (open.length) {
    return open.sort((a, b) =>
      String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || ''))
    )[0];
  }
  return list[0] || null;
}

export function hasOpenInspection(jobsForSs) {
  const job = pickOpenJob(jobsForSs);
  return !!(job && job.status && !CLOSED.has(job.status));
}

/** Active SS with a due date and no open work pack ?? the only place Issue manually belongs. */
export function countMissingOpenInspections(ssList, jobsBySs) {
  const map = jobsBySs instanceof Map ? jobsBySs : new Map();
  return (ssList || []).filter((inst) => {
    if (inst.status && inst.status !== 'ACTIVE') return false;
    const open = pickOpenJob(map.get(inst.ss_code) || []);
    const hasOpen = open && open.status && !CLOSED.has(open.status);
    return !hasOpen && toYmd(inst.nextDueDate || inst.next_due_date || inst.due_date);
  }).length;
}

export function formatIqpName(row) {
  const name = String(row?.default_assignee_name || row?.assigned_worker_name || '').trim();
  if (!name) return '';
  const kind =
    row.default_assignee_type === 'EXTERNAL_COMPANY'
      ? 'Ext'
      : row.default_assignee_type === 'INTERNAL_IQP'
        ? 'IQP'
        : '';
  return kind ? `${name} · ${kind}` : name;
}

export function summarizeIqpNames(rows) {
  const counts = new Map();
  (rows || []).forEach((r) => {
    const name = String(r.default_assignee_name || r.assigned_worker_name || '').trim();
    if (!name) return;
    counts.set(name, (counts.get(name) || 0) + 1);
  });
  const parts = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, n]) => (n > 1 ? `${name} (${n})` : name));
  return parts;
}

/**
 * Create one inspection job. Assigns the SS default IQP when a worker id is present.
 */
export async function createInspectionJob({
  buildingId,
  ssCode,
  ssName,
  dueDate,
  inspectionFrequency,
  assignedWorkerId,
}) {
  const due = toYmd(dueDate);
  if (!buildingId || !ssCode || !due) {
    throw new Error(`Cannot order ${ssCode || 'SS'}: missing building, SS, or due date`);
  }
  const payload = {
    buildingId: Number(buildingId),
    ssCode,
    ssName: ssName || ssCode,
    dueDate: due,
    inspectionType: 'ANNUAL',
    inspectionFrequency: inspectionFrequency || 'ANNUAL',
  };
  if (assignedWorkerId) payload.assignedWorkerId = assignedWorkerId;
  const response = await api.post('/jobs', payload);
  return response.data;
}

/**
 * Order inspections for SS rows that do not already have an open job.
 * @param {Array} items { ss_code, ss_name, due_date|nextDueDate, inspection_frequency, default_worker_id, skip? }
 */
export async function orderMissingInspections({ buildingId, items }) {
  const created = [];
  const skipped = [];
  for (const inst of items || []) {
    const code = inst.ss_code || inst.ssCode;
    if (!code || inst.skip) {
      skipped.push(code || '(blank)');
      continue;
    }
    const due = toYmd(inst.nextDueDate || inst.due_date || inst.dueDate);
    if (!due) {
      skipped.push(`${code} (no due date)`);
      continue;
    }
    const data = await createInspectionJob({
      buildingId,
      ssCode: code,
      ssName: inst.ss_name || inst.ssName || inst.location || code,
      dueDate: due,
      inspectionFrequency: inst.inspection_frequency || inst.inspectionFrequency || 'ANNUAL',
      assignedWorkerId: inst.default_worker_id || inst.assigned_worker_id || null,
    });
    if (data?.id) created.push({ ss: code, id: data.id });
  }
  return { created: created.length, items: created, skipped };
}
